/**
 * /dashboard/hr — HR module Overview (Phase 3c — 2026-06-01).
 *
 * Pattern E sub-app — the strip in HrModuleShell handles cross-section
 * navigation (Overview / Onboarding / Documents / Assets). This Overview
 * page surfaces the HR-Core destinations in tile form for users landing
 * on /dashboard/hr from the main sidebar. The shell wraps in
 * HrFeatureGate; this page does not need its own.
 *
 * The Onboarding-templates tile is HR-admin only — those moved to
 * Settings (SOP §8.3) but until that surface lands the tile here keeps
 * the discoverability the Next.js Overview had. Once Settings (Phase
 * 3g) ports, this can be revisited.
 *
 * Migration note (2026-06-01, Next.js → Vite): port of
 * frontend/src/app/(dashboard)/dashboard/hr/page.tsx flattened with the
 * old `layout.tsx` into one route that uses HrModuleShell. Swaps:
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link
 *
 * The onboarding-templates link target (/dashboard/hr/onboarding/templates)
 * is a Settings-bound surface that has NOT been registered yet; the
 * link is typed as a plain string fallback (TODO: switch to the typed
 * route once Phase 3g lands it under /dashboard/settings/onboarding/templates).
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  ClipboardList,
  FileText,
  Package,
  Settings2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { HrFeatureGate } from "@/components/hr/hr-feature-gate";
import { HrModuleShell } from "@/components/shared/hr-module-shell";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/hr/")({
  component: HrOverviewRoute,
});

interface HubCard {
  // TODO Phase 3g: switch to typed route once the templates / Settings
  // surface is registered. For now: a free-string href so an unregistered
  // route doesn't break compile, surfaced via window.location only when
  // the user clicks (which 404s gracefully on this Vite SPA build).
  href: string;
  title: string;
  description: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  /** When the target IS a registered typed route, use Link to=… */
  typed?: true;
}

const CARDS: HubCard[] = [
  {
    href: "/app/$tenantSlug/hr/onboarding",
    title: "Onboarding",
    description:
      "Track onboarding checklists. Employees see their own progress; managers and HR see their teams.",
    icon: ClipboardList,
    typed: true,
  },
  {
    href: "/app/$tenantSlug/hr/onboarding/templates",
    title: "Onboarding templates",
    description:
      "Create and manage reusable onboarding checklists, and assign them to employees.",
    icon: Settings2,
    adminOnly: true,
  },
  {
    href: "/app/$tenantSlug/hr/documents",
    title: "Documents",
    description:
      "Employee document store with visibility controls and expiry tracking.",
    icon: FileText,
    typed: true,
  },
  {
    href: "/app/$tenantSlug/hr/assets",
    title: "Asset register",
    description:
      "The company asset register — assign, return, retire or report lost.",
    icon: Package,
    typed: true,
  },
];

function HubTile({ card }: { card: HubCard }) {
  const Icon = card.icon;
  const inner = (
    <Card className="h-full transition-colors group-hover:border-primary/40">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-accent-foreground">
            <Icon className="h-5 w-5" />
          </span>
          <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
        </div>
        <CardTitle className="mt-3 text-base">{card.title}</CardTitle>
        <CardDescription>{card.description}</CardDescription>
      </CardHeader>
      <CardContent />
    </Card>
  );

  if (card.typed) {
    // Compile-checked typed link — href is one of the registered HR
    // routes (/dashboard/hr/onboarding, /dashboard/hr/documents,
    // /dashboard/hr/assets).
    return (
      <Link to={card.href} className="group block">
        {inner}
      </Link>
    );
  }
  // TODO: switch to typed Link once Phase 3g registers the templates
  // settings surface. <a> is used so the build doesn't fail on an
  // unregistered route literal.
  return (
    <a href={card.href} className="group block">
      {inner}
    </a>
  );
}

function HrOverviewRoute() {
  const { isHrAdmin } = useHrRoles();
  const cards = CARDS.filter((c) => !c.adminOnly || isHrAdmin);

  return (
    <HrModuleShell
      title="Overview"
      description="Onboarding, employee documents and the asset register."
    >
      <HrFeatureGate>
        <div className="grid gap-4 sm:grid-cols-2">
          {cards.map((c) => (
            <HubTile key={c.href} card={c} />
          ))}
        </div>
      </HrFeatureGate>
    </HrModuleShell>
  );
}
